/** 
 * DISCOUNT_PAINTERS_LandingPage_v01_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v01
 * Date: 2025-11-25 (America/Denver)
 *
 * Summary:
 * - Based on EASY_RENTERS_LandingPage_v02O_WIZARD structure & methodology.
 * - Wizard & vertical modes with blur-driven validation.
 * - All-string wire payload conversion; sectioned confirmation, no JSON dump.
 * - Tailored for painting contractors (DiscountPaintersInsurance.com).
 */

import React, { useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  Carousel,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

import { MetaTags } from "../../component-library/UTILITY-COMPONENTS/META-TAGS";
import { paintersInsuranceFAQs } from "../DUI-Insurance/FAQ_PaintersInsurance_v01";
import { FAQ_Component_v02b_Simple } from "../../component-library/FAQ_Component_v02b-simple";
import InsuranceCarriersList_v01 from "../../Insurance-Legacy/InsuranceCarriersList_v02";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v01a";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const AGENT_PHONE_HOURS = "M-F  9am to 5pm MT";
const imagePath = "insurance/images/PAINTERS-INSURANCE/";

// ONE CUSTOM BUTTON ON FORM SUBMISSION CONFIRMATION
const AGENT_PHONE_BUTTON_OBJ = (
  <>
    <div className="d-none d-md-block text-body-secondary me-2">Speak with an agent:</div>
    <OverlayTrigger
      placement="right"
      overlay={<Tooltip id="NumberOfEmployeesTip">{AGENT_PHONE_HOURS}</Tooltip>}
    >
      <Button variant="outline-primary" href="tel:3033375434">
        {AGENT_PHONE_NUMBER}
      </Button>
    </OverlayTrigger>
  </>
);

/* ============================================================
   TYPES
============================================================ */
type WorkFocus = "" | "residential" | "commercial" | "both";
type GLimit = "" | "300000" | "500000" | "1000000" | "2000000";

/** Raw lead payload (typed for app state) */
interface IPaintersLeadPayload {
  // Contact
  fullName: string;
  email: string;
  phone: string;

  // Business & location
  businessName: string;
  website?: string;

  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;

  yearsInBusiness: number;
  employeesCount: number;
  workFocus: WorkFocus;
  residentialPercent: number;
  commercialPercent: number;
  workRadiusMiles: number;

  // Work & exposures
  servicesOffered?: string;
  highRiskWork?: string;
  doesInteriorWork: boolean;
  doesExteriorWork: boolean;
  worksAboveTwoStories: boolean;

  // Coverage needs
  generalLiabilityLimit: GLimit;
  wantsWorkersComp: boolean;
  wantsCommercialAuto: boolean;
  wantsToolsEquipment: boolean;

  currentCarrierName?: string;
  currentPolicyExpiration?: string;
  priorClaimsCount5y: number;

  notes?: string;

  utm?: { source?: string; medium?: string; campaign?: string };

  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

function digitsOnly(s: string): string {
  return (s ?? "").replace(/\D+/g, "");
}
function isValidEmail(email: string): boolean {
  return /.+@.+\..+/.test(email);
}
function isValidPhone(raw: string): boolean {
  return digitsOnly(raw).length === 10;
}
function isValidZipCode(raw: string): boolean {
  return digitsOnly(raw).length === 5;
}
function formatPhonePretty(raw: string): string {
  const d = digitsOnly(raw);
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
function cap(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
function money(nLike: string) {
  const n = Number(nLike);
  if (!Number.isFinite(n)) return nLike;
  try {
    return n.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  } catch {
    return `$${n}`;
  }
}
const dash = (v?: string) => (v && v.trim().length ? v : "—");

/* ============================================================
   SLIDES
============================================================ */
interface Slide {
  src: string;
  alt: string;
  title: string;
  subline: string;
  smallCaption: string;
}

const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_438756560_CEILING.FLIP.jpg",
    alt: "Professional painter painting ceiling with roller.",
    title: "Protect Your Painting Business",
    subline: "Affordable liability, tools, and vehicle coverage for painters and paint contractors.",
    smallCaption: "Fast quotes. Clear options.",
  },
  {
    src: imagePath + "AdobeStock_144928014_TAPE.jpg",
    alt: "A professional placing painters tape on a home interior.",
    title: "From Small Jobs to Big Projects",
    subline: "Tell us about your typical jobs and we’ll match the coverage to your actual risk to ensure your are covered.",
    smallCaption: "Built for growing crews.",
  },
  {
    src: imagePath + "AdobeStock_167531395_ROOF.jpg",
    alt: "Painting company truck with ladders and equipment.",
    title: "Protect Vans, Ladders & Gear",
    subline: "Bundle commercial auto and tools coverage with your liability policy.",
    smallCaption: "Smart bundling. Better value.",
  },
  {
    src: imagePath + "AdobeStock_272846176_LATTER.jpg",
    alt: "Painter on a ladder.",
    title: "Licensed Advisors, Real Answers",
    subline: "Talk with someone who understands painter risks and certificates of insurance.",
    smallCaption: "COIs made easy.",
  },
  {
    src: imagePath + "AdobeStock_524514142_CPICKER.jpg",
    alt: "Painting company truck with ladders and equipment.",
    title: "Licensed Advisors, Real Answers",
    subline: "From Small Jobs to Big Projects.",
    smallCaption: "COIs made easy.",
  },
];

/* ============================================================
   MAIN
============================================================ */
export const DISCOUNT_PAINTERS_LandingPage_v01_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Business & location
  const [businessName, setBusinessName] = useState("");
  const [website, setWebsite] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Business profile
  const [yearsInBusiness, setYearsInBusiness] = useState<number>(0);
  const [employeesCount, setEmployeesCount] = useState<number>(1);
  const [workFocus, setWorkFocus] = useState<WorkFocus>("");
  const [residentialPercent, setResidentialPercent] = useState<number>(80);
  const [commercialPercent, setCommercialPercent] = useState<number>(20);
  const [workRadiusMiles, setWorkRadiusMiles] = useState<number>(50);

  // Work & exposures
  const [servicesOffered, setServicesOffered] = useState<string>("");
  const [highRiskWork, setHighRiskWork] = useState<string>("");
  const [doesInteriorWork, setDoesInteriorWork] = useState<boolean>(true);
  const [doesExteriorWork, setDoesExteriorWork] = useState<boolean>(true);
  const [worksAboveTwoStories, setWorksAboveTwoStories] = useState<boolean>(false);

  // Coverage needs
  const [generalLiabilityLimit, setGeneralLiabilityLimit] = useState<GLimit>("1000000");
  const [wantsWorkersComp, setWantsWorkersComp] = useState<boolean>(true);
  const [wantsCommercialAuto, setWantsCommercialAuto] = useState<boolean>(true);
  const [wantsToolsEquipment, setWantsToolsEquipment] = useState<boolean>(true);

  const [currentCarrierName, setCurrentCarrierName] = useState<string>("");
  const [currentPolicyExpiration, setCurrentPolicyExpiration] = useState<string>("");
  const [priorClaimsCount5y, setPriorClaimsCount5y] = useState<number>(0);

  // Misc
  const [notes, setNotes] = useState<string>("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] =
    useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);

  // Keep the exact data sent (for confirmation display)
  const [lastSubmittedApp, setLastSubmittedApp] = useState<IPaintersLeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact & Business", "Coverage & Exposures", "Details & Notes", "Confirmation"] as const;

  /* ---------- Touched + validation helpers (blur-driven) ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });

  // Optional-field validation props: green when filled & valid; neutral when blank; never red on blank.
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  // Validators (blank optional fields pass)
  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),

    businessName: businessName.trim().length >= 2,

    address1: address1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: isValidZipCode(zipCode),

    yearsInBusiness:
      Number.isFinite(yearsInBusiness) && yearsInBusiness >= 0 && yearsInBusiness <= 100,
    employeesCount:
      Number.isFinite(employeesCount) && employeesCount >= 0 && employeesCount <= 500,
    workFocus: workFocus !== "",

    residentialPercent:
      Number.isFinite(residentialPercent) &&
      residentialPercent >= 0 &&
      residentialPercent <= 100,
    commercialPercent:
      Number.isFinite(commercialPercent) &&
      commercialPercent >= 0 &&
      commercialPercent <= 100,
    workRadiusMiles:
      Number.isFinite(workRadiusMiles) && workRadiusMiles > 0 && workRadiusMiles <= 250,

    generalLiabilityLimit: generalLiabilityLimit !== "",

    priorClaimsCount5y:
      Number.isFinite(priorClaimsCount5y) && priorClaimsCount5y >= 0 && priorClaimsCount5y <= 20,

    // optional
    website: !website || website.trim().length >= 4,
    servicesOffered:
      servicesOffered.trim().length === 0 || servicesOffered.trim().length >= 3,
    highRiskWork: highRiskWork.trim().length === 0 || highRiskWork.trim().length >= 3,
    currentCarrierName:
      !currentCarrierName || currentCarrierName.trim().length >= 2,
    currentPolicyExpiration:
      !currentPolicyExpiration || currentPolicyExpiration.trim().length >= 4,
    notes: notes.trim().length === 0 || notes.trim().length >= 2,
  };

  const valid = useMemo(() => {
    return (
      V.fullName &&
      V.email &&
      V.phone &&
      V.businessName &&
      V.address1 &&
      V.city &&
      V.state &&
      V.zip &&
      V.yearsInBusiness &&
      V.employeesCount &&
      V.workFocus &&
      V.residentialPercent &&
      V.commercialPercent &&
      V.workRadiusMiles &&
      V.generalLiabilityLimit &&
      V.priorClaimsCount5y &&
      V.website &&
      V.servicesOffered &&
      V.highRiskWork &&
      V.currentCarrierName &&
      V.currentPolicyExpiration &&
      V.notes
    );
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return (
          V.fullName &&
          V.email &&
          V.phone &&
          V.businessName &&
          V.address1 &&
          V.city &&
          V.state &&
          V.zip &&
          V.yearsInBusiness &&
          V.employeesCount &&
          V.workFocus
        );
      case 1:
        return (
          V.generalLiabilityLimit &&
          V.residentialPercent &&
          V.commercialPercent &&
          V.workRadiusMiles &&
          V.priorClaimsCount5y
        );
      case 2:
        return (
          V.website &&
          V.servicesOffered &&
          V.highRiskWork &&
          V.currentCarrierName &&
          V.currentPolicyExpiration &&
          V.notes
        );
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) =>
      setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));

    if (currentStep === 0) {
      touchMany([
        "fullName",
        "email",
        "phone",
        "businessName",
        "address1",
        "city",
        "state",
        "zip",
        "yearsInBusiness",
        "employeesCount",
        "workFocus",
      ]);
    } else if (currentStep === 1) {
      touchMany([
        "generalLiabilityLimit",
        "residentialPercent",
        "commercialPercent",
        "workRadiusMiles",
        "priorClaimsCount5y",
      ]);
    } else if (currentStep === 2) {
      touchMany([
        "website",
        "servicesOffered",
        "highRiskWork",
        "currentCarrierName",
        "currentPolicyExpiration",
        "notes",
      ]);
    }

    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };

  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ============================================================
     PAYLOAD & SUBMIT
  ============================================================ */

  function buildLeadPayload(): IPaintersLeadPayload {
    return {
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),

      businessName: businessName.trim(),
      website: website?.trim() || "",

      address1: address1.trim(),
      address2: address2?.trim() || "",
      city: city.trim(),
      state: String(state),
      zipCode: String(zipCode),


      
      yearsInBusiness: Number(yearsInBusiness),
      employeesCount: Number(employeesCount),
      workFocus,

      residentialPercent: Number(residentialPercent),
      commercialPercent: Number(commercialPercent),
      workRadiusMiles: Number(workRadiusMiles),

      servicesOffered: servicesOffered?.trim() || "",
      highRiskWork: highRiskWork?.trim() || "",
      doesInteriorWork,
      doesExteriorWork,
      worksAboveTwoStories,

      generalLiabilityLimit,
      wantsWorkersComp,
      wantsCommercialAuto,
      wantsToolsEquipment,

      currentCarrierName: currentCarrierName?.trim() || "",
      currentPolicyExpiration: currentPolicyExpiration?.trim() || "",
      priorClaimsCount5y: Number(priorClaimsCount5y),

      notes: notes?.trim() || "",

      domainName:
        typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.painters.quote",
      itemClass: "quote.request",
      itemType: "legacy.painters.landing",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: IPaintersLeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") {
        out[k] = v;
      } else if (typeof v === "number") {
        out[k] = Number.isFinite(v) ? `${v}` : "";
      } else if (typeof v === "boolean") {
        out[k] = v ? "true" : "false";
      } else if (v && typeof v === "object") {
        try {
          out[k] = JSON.stringify(v);
        } catch {
          out[k] = "";
        }
      } else {
        out[k] = "";
      }
    }
    return out;
  }

  async function postLead(
    payload: WirePayload
  ): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try {
      const txt = await res.text();
      resJson = txt ? JSON.parse(txt) : null;
    } catch {
      resJson = null;
    }

    const asObj = (x: unknown): Record<string, unknown> | null =>
      x && typeof x === "object" && !Array.isArray(x)
        ? (x as Record<string, unknown>)
        : null;

    if (!res.ok) {
      const o = asObj(resJson);
      const msg =
        (o?.error as string) ||
        (o?.message as string) ||
        `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }

    const o = asObj(resJson);
    const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    // In wizard mode, ignore submit on steps 0–1 and just advance
    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // Touch all fields so feedback appears if invalid
    setTouched((t) => ({
      ...t,
      fullName: true,
      email: true,
      phone: true,
      businessName: true,
      address1: true,
      city: true,
      state: true,
      zip: true,
      yearsInBusiness: true,
      employeesCount: true,
      workFocus: true,
      residentialPercent: true,
      commercialPercent: true,
      workRadiusMiles: true,
      generalLiabilityLimit: true,
      priorClaimsCount5y: true,
      website: true,
      servicesOffered: true,
      highRiskWork: true,
      currentCarrierName: true,
      currentPolicyExpiration: true,
      notes: true,
    }));

    if (!valid) {
      setSubmitError("Please complete all required fields.");
      return;
    }

    const appPayload = buildLeadPayload();
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 600));
        const status = {
          ok: true,
          status: 200,
          id: "TEST-" + Math.random().toString(36).slice(2, 8),
        };
        setServerStatus(status);
        setLastSubmittedApp(appPayload);
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const status = await postLead(wirePayload);
      setServerStatus(status);

      if (!status.ok) throw new Error(status.message || "Unknown server error");

      setLastSubmittedApp(appPayload);
      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Clamp the active slide index
  const active =
    slides[
      Math.min(
        slides.length - 1,
        Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0)
      )
    ];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <>
      <MetaTags
        title="DiscountPaintersInsurance.com - Affordable Painters & Contractors Insurance | Compare Quotes Online"
        description="Save on painters liability, commercial auto, and tools coverage. Get free quotes for painting contractors in minutes. Fast, affordable, hassle-free painter insurance."
        canonicalUrl="https://www.discountpaintersinsurance.com/"
        imageUrl="https://www.discountpaintersinsurance.com/images/og-image.jpg"
      />

      <div className="bg-body">
        {/* Sticky Topbar */}
        <Navbar expand="md" bg="light" className="border-bottom sticky-top">
          <Container>
            <Navbar.Brand href="#" className="fw-bold">
              Discount <span className="text-primary">Painters</span> Insurance
              {SHOW_VERSION && (
                <Badge bg="secondary" className="ms-2">
                  {VERSION}
                </Badge>
              )}
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="topbar" />
            <Navbar.Collapse id="topbar" className="justify-content-end">
              <Nav className="align-items-center gap-2">
                {AGENT_PHONE_BUTTON_OBJ}
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>

        {/* HERO with background Carousel */}
        <section
          id="home"
          className="position-relative overflow-hidden"
          style={{ minHeight: "760px" }}
        >
          <div
            className="position-absolute top-0 start-0 end-0 bottom-0"
            style={{ zIndex: 0 }}
            aria-hidden
          >
            <Carousel
              activeIndex={activeSlide}
              onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
              controls
              indicators
              fade
              interval={3500}
              className="w-100 h-100"
            >
              {slides.map((s, idx) => (
                <Carousel.Item key={idx} className="w-100 h-100">
                  <div
                    role="img"
                    aria-label={s.alt}
                    style={{
                      width: "100%",
                      height: "100%",
                      minHeight: 760,
                      backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <Carousel.Caption
                    className="text-start"
                    style={{ bottom: "40%" }}
                  />
                </Carousel.Item>
              ))}
            </Carousel>
          </div>

          {/* Foreground content */}
          <Container className="position-relative" style={{ zIndex: 1 }}>
            <Row className="align-items-start g-0 g-sm-4 p-0 p-sm-4 p-lg-5">
              <Col lg={6} className="text-white">
                <div className="pe-lg-4">
                  <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>
                    {active.title}
                  </h1>
                  <p className="lead mb-3">{active.subline}</p>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Badge bg="light" text="dark">
                      Painters Liability
                    </Badge>
                    <Badge bg="light" text="dark">
                      Commercial Auto
                    </Badge>
                    <Badge bg="light" text="dark">
                      Tools &amp; Equipment
                    </Badge>
                  </div>
                  <div className="mt-4 d-flex gap-2">
                    <Button variant="primary" onClick={scrollToForm}>
                      Get My Painters Quote
                    </Button>
                    <Button variant="outline-light" href="#faq">
                      Common Questions
                    </Button>
                  </div>
                </div>
              </Col>
              <Col lg={6}>
                <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                  <Card.Header className="bg-white">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <div className="fw-semibold">Fast, No-Obligation Quote</div>
                        <small className="text-body-secondary">
                          Tell us about your painting business to see options.
                        </small>
                      </div>
                      <Badge bg="success">Secure</Badge>
                    </div>
                  </Card.Header>
                  <Card.Body ref={formRef}>
                    {submitError && (
                      <Alert
                        variant="danger"
                        onClose={() => setSubmitError(null)}
                        dismissible
                      >
                        Server: {submitError}
                      </Alert>
                    )}

                    {/* MODE TOGGLE + (Wizard) Stepper */}
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="switch"
                          id="modeSwitch"
                          label={isWizard ? "Wizard mode" : "Vertical mode"}
                          checked={isWizard}
                          onChange={(e) => {
                            setMode(e.currentTarget.checked ? "wizard" : "vertical");
                            setTouched({});
                            if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                            else setCurrentStep(0);
                          }}
                        />
                        <small className="text-muted">
                          Switch to see all fields in one view.
                        </small>
                      </div>

                      <div style={{ minWidth: 280 }}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <Badge bg="secondary">
                            Step{" "}
                            {isWizard ? currentStep + 1 : submitOk ? 2 : 1} of{" "}
                            {isWizard ? 4 : submitOk ? 2 : 1}
                          </Badge>
                          <small className="text-muted">
                            {isWizard
                              ? steps[currentStep]
                              : submitOk
                              ? "Confirmation"
                              : "All fields"}
                          </small>
                        </div>
                        <ProgressBar
                          now={
                            isWizard
                              ? ((currentStep + 1) / 4) * 100
                              : submitOk
                              ? 100
                              : 50
                          }
                        />
                      </div>
                    </div>

                    {/* PAINTERS FORM START */}
                    <Form
                      onSubmit={handleSubmit}
                      noValidate
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && isWizard) {
                          if (currentStep < 2) {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isStepValid(currentStep)) goNext();
                          }
                        }
                      }}
                    >
                      {isWizard ? (
                        <>
                          <Row className="g-3">
                            {/* STEP 1: CONTACT & BUSINESS */}
                            {currentStep === 0 && (
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend
                                    className="px-2 fs-6 text-muted"
                                    style={{
                                      top: "0.5rem",
                                      background: "var(--bs-secondary-bg)",
                                    }}
                                  >
                                    <strong>Contact &amp; Business</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={12}>
                                      <Form.Label htmlFor="fullName">
                                        Your Name
                                      </Form.Label>
                                      <Form.Control
                                        id="fullName"
                                        value={fullName}
                                        onChange={(e) =>
                                          setFullName(e.target.value)
                                        }
                                        placeholder="Jane Contractor"
                                        required
                                        {...vp("fullName", V.fullName)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Please enter your name.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="email">Email</Form.Label>
                                      <Form.Control
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) =>
                                          setEmail(e.target.value)
                                        }
                                        placeholder="you@company.com"
                                        required
                                        {...vp("email", V.email)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Enter a valid email.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="phone">Phone</Form.Label>
                                      <Form.Control
                                        id="phone"
                                        inputMode="tel"
                                        value={phone}
                                        onChange={(e) =>
                                          setPhone(e.target.value)
                                        }
                                        placeholder="(555) 123-4567"
                                        required
                                        {...vp("phone", V.phone)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Enter a 10-digit phone.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={8}>
                                      <Form.Label htmlFor="businessName">
                                        Painting Business Name
                                      </Form.Label>
                                      <Form.Control
                                        id="businessName"
                                        value={businessName}
                                        onChange={(e) =>
                                          setBusinessName(e.target.value)
                                        }
                                        placeholder="ABC Painting, LLC"
                                        required
                                        {...vp("businessName", V.businessName)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Please enter your business name.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="website">
                                        Website (optional)
                                      </Form.Label>
                                      <Form.Control
                                        id="website"
                                        value={website}
                                        onChange={(e) =>
                                          setWebsite(e.target.value)
                                        }
                                        placeholder="www.abcpainting.com"
                                        {...vpOpt(
                                          "website",
                                          !!website.trim(),
                                          V.website
                                        )}
                                      />
                                    </Col>

                                    <Col md={8}>
                                      <Form.Label htmlFor="address1">
                                        Business Street Address
                                      </Form.Label>
                                      <Form.Control
                                        id="address1"
                                        value={address1}
                                        onChange={(e) =>
                                          setAddress1(e.target.value)
                                        }
                                        placeholder="123 Main St"
                                        required
                                        {...vp("address1", V.address1)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Enter your street address.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="address2">
                                        Suite / Unit (optional)
                                      </Form.Label>
                                      <Form.Control
                                        id="address2"
                                        value={address2}
                                        onChange={(e) =>
                                          setAddress2(e.target.value)
                                        }
                                        placeholder="Suite 200"
                                        {...vpOpt(
                                          "address2",
                                          !!address2.trim()
                                        )}
                                      />
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="city">City</Form.Label>
                                      <Form.Control
                                        id="city"
                                        value={city}
                                        onChange={(e) =>
                                          setCity(e.target.value)
                                        }
                                        placeholder="City"
                                        required
                                        {...vp("city", V.city)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Enter your city.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="state">State</Form.Label>
                                      <Form.Select
                                        id="state"
                                        value={state}
                                        onChange={(e) =>
                                          setState(e.target.value)
                                        }
                                        required
                                        {...vp("state", V.state)}
                                      >
                                        <option value="">Select</option>
                                        {US_STATES.map((s) => (
                                          <option
                                            key={s.code}
                                            value={s.code}
                                          >
                                            {s.name}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="zipCode">Zip</Form.Label>
                                      <Form.Control
                                        id="zipCode"
                                        inputMode="numeric"
                                        value={zipCode}
                                        onChange={(e) =>
                                          setZipCode(e.target.value)
                                        }
                                        placeholder="#####"
                                        required
                                        {...vp("zip", V.zip)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Enter a 5-digit zip code.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="yearsInBusiness">
                                        Years in Business
                                      </Form.Label>
                                      <Form.Control
                                        id="yearsInBusiness"
                                        inputMode="numeric"
                                        value={yearsInBusiness}
                                        onChange={(e) =>
                                          setYearsInBusiness(
                                            Number(e.target.value)
                                          )
                                        }
                                        placeholder="0"
                                        required
                                        {...vp(
                                          "yearsInBusiness",
                                          V.yearsInBusiness
                                        )}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        0–100 years allowed.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="employeesCount">
                                        # of Employees
                                      </Form.Label>
                                      <Form.Control
                                        id="employeesCount"
                                        inputMode="numeric"
                                        value={employeesCount}
                                        onChange={(e) =>
                                          setEmployeesCount(
                                            Number(e.target.value)
                                          )
                                        }
                                        placeholder="1"
                                        required
                                        {...vp(
                                          "employeesCount",
                                          V.employeesCount
                                        )}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        0–500 allowed.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="workFocus">
                                        Main Type of Work
                                      </Form.Label>
                                      <Form.Select
                                        id="workFocus"
                                        value={workFocus}
                                        onChange={(e) =>
                                          setWorkFocus(
                                            e.target.value as WorkFocus
                                          )
                                        }
                                        required
                                        {...vp("workFocus", V.workFocus)}
                                      >
                                        <option value="">Select</option>
                                        <option value="residential">
                                          Residential
                                        </option>
                                        <option value="commercial">
                                          Commercial
                                        </option>
                                        <option value="both">
                                          Both
                                        </option>
                                      </Form.Select>
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            )}

                            {/* STEP 2: COVERAGE & EXPOSURES */}
                            {currentStep === 1 && (
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend
                                    className="px-2 fs-6 text-muted"
                                    style={{
                                      top: "0.5rem",
                                      background: "var(--bs-secondary-bg)",
                                    }}
                                  >
                                    <strong>Coverage &amp; Exposures</strong>
                                  </legend>





                                  <Row className="g-3 mt-1">
                                    <Col md={12}>
                                      <Form.Label htmlFor="glLimit">
                                        Desired General Liability Limit
                                      </Form.Label>
                                      <Form.Select
                                        id="glLimit"
                                        value={generalLiabilityLimit}
                                        onChange={(e) =>
                                          setGeneralLiabilityLimit(
                                            e.target.value as GLimit
                                          )
                                        }
                                        required
                                        {...vp(
                                          "generalLiabilityLimit",
                                          V.generalLiabilityLimit
                                        )}
                                      >
                                        <option value="">Select</option>
                                        <option value="300000">
                                          $300,000
                                        </option>
                                        <option value="500000">
                                          $500,000
                                        </option>
                                        <option value="1000000">
                                          $1,000,000
                                        </option>
                                        <option value="2000000">
                                          $2,000,000
                                        </option>
                                       
                                      </Form.Select>
                                      <div className="form-text">
                                        Typical painters carry at least $1M.
                                      </div>
                                    </Col>






                                    <Col md={6}>
                                      <Form.Label htmlFor="resPct">
                                        Residential (% of jobs)
                                      </Form.Label>
                                      <Form.Control
                                        id="resPct"
                                        inputMode="numeric"
                                        value={residentialPercent}
                                        onChange={(e) =>
                                          setResidentialPercent(
                                            Number(e.target.value)
                                          )
                                        }
                                        placeholder="80"
                                        required
                                        {...vp(
                                          "residentialPercent",
                                          V.residentialPercent
                                        )}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        0–100 allowed.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="comPct">
                                        Commercial (% of jobs)
                                      </Form.Label>
                                      <Form.Control
                                        id="comPct"
                                        inputMode="numeric"
                                        value={commercialPercent}
                                        onChange={(e) =>
                                          setCommercialPercent(
                                            Number(e.target.value)
                                          )
                                        }
                                        placeholder="20"
                                        required
                                        {...vp(
                                          "commercialPercent",
                                          V.commercialPercent
                                        )}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        0–100 allowed.
                                      </Form.Control.Feedback>
                                      <div className="form-text">
                                        (Doesn't have to total 100 exactly.)
                                      </div>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="radius">
                                        Typical Work Radius (miles)
                                      </Form.Label>
                                      <Form.Control
                                        id="radius"
                                        inputMode="numeric"
                                        value={workRadiusMiles}
                                        onChange={(e) =>
                                          setWorkRadiusMiles(
                                            Number(e.target.value)
                                          )
                                        }
                                        placeholder="50"
                                        required
                                        {...vp(
                                          "workRadiusMiles",
                                          V.workRadiusMiles
                                        )}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        1–250 allowed.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="claims">
                                        Prior Claims (last 5 years)
                                      </Form.Label>
                                      <Form.Control
                                        id="claims"
                                        inputMode="numeric"
                                        value={priorClaimsCount5y}
                                        onChange={(e) =>
                                          setPriorClaimsCount5y(
                                            Number(e.target.value)
                                          )
                                        }
                                        placeholder="0"
                                        required
                                        {...vp(
                                          "priorClaimsCount5y",
                                          V.priorClaimsCount5y
                                        )}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        0–20 allowed.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label>Coverages Needed</Form.Label>
                                      <div className="d-flex flex-column gap-1 pt-1">
                                        <Form.Check
                                          id="workersComp"
                                          type="checkbox"
                                          label="Workers Compensation"
                                          checked={wantsWorkersComp}
                                          onChange={(e) =>
                                            setWantsWorkersComp(
                                              e.currentTarget.checked
                                            )
                                          }
                                        />
                                        <Form.Check
                                          id="commAuto"
                                          type="checkbox"
                                          label="Commercial Auto"
                                          checked={wantsCommercialAuto}
                                          onChange={(e) =>
                                            setWantsCommercialAuto(
                                              e.currentTarget.checked
                                            )
                                          }
                                        />
                                        <Form.Check
                                          id="toolsEquip"
                                          type="checkbox"
                                          label="Tools &amp; Equipment"
                                          checked={wantsToolsEquipment}
                                          onChange={(e) =>
                                            setWantsToolsEquipment(
                                              e.currentTarget.checked
                                            )
                                          }
                                        />
                                      </div>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label>Where You Work</Form.Label>
                                      <div className="d-flex flex-column gap-1 pt-1">
                                        <Form.Check
                                          id="interiorWork"
                                          type="checkbox"
                                          label="Interior Work"
                                          checked={doesInteriorWork}
                                          onChange={(e) =>
                                            setDoesInteriorWork(
                                              e.currentTarget.checked
                                            )
                                          }
                                        />
                                        <Form.Check
                                          id="exteriorWork"
                                          type="checkbox"
                                          label="Exterior Work"
                                          checked={doesExteriorWork}
                                          onChange={(e) =>
                                            setDoesExteriorWork(
                                              e.currentTarget.checked
                                            )
                                          }
                                        />
                                        <Form.Check
                                          id="aboveTwoStories"
                                          type="checkbox"
                                          label="Work Above 2 Stories"
                                          checked={worksAboveTwoStories}
                                          onChange={(e) =>
                                            setWorksAboveTwoStories(
                                              e.currentTarget.checked
                                            )
                                          }
                                        />
                                      </div>
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            )}

                            {/* STEP 3: DETAILS & NOTES */}
                            {currentStep === 2 && (
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend
                                    className="px-2 fs-6 text-muted"
                                    style={{
                                      top: "0.5rem",
                                      background: "var(--bs-secondary-bg)",
                                    }}
                                  >
                                    <strong>Details, Carrier &amp; Notes</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="servicesOffered">
                                        Types of Painting You Do (optional)
                                      </Form.Label>
                                      <Form.Control
                                        id="servicesOffered"
                                        as="textarea"
                                        rows={3}
                                        value={servicesOffered}
                                        onChange={(e) =>
                                          setServicesOffered(e.target.value)
                                        }
                                        placeholder="e.g., Interior, exterior, cabinets, decks, new construction, repaints..."
                                        {...vp(
                                          "servicesOffered",
                                          V.servicesOffered
                                        )}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Please enter at least 3 characters or leave
                                        it blank.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="highRiskWork">
                                        Any high-risk work? (optional)
                                      </Form.Label>
                                      <Form.Control
                                        id="highRiskWork"
                                        as="textarea"
                                        rows={3}
                                        value={highRiskWork}
                                        onChange={(e) =>
                                          setHighRiskWork(e.target.value)
                                        }
                                        placeholder="e.g., High-rise, industrial plants, lead paint removal..."
                                        {...vp("highRiskWork", V.highRiskWork)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Please enter at least 3 characters or leave
                                        it blank.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="currentCarrierName">
                                        Current Insurance Company 
                                      </Form.Label>
                                      <Form.Control
                                        id="currentCarrierName"
                                        value={currentCarrierName}
                                        onChange={(e) =>
                                          setCurrentCarrierName(e.target.value)
                                        }
                                        placeholder="If currently insured"
                                        {...vp(
                                          "currentCarrierName",
                                          V.currentCarrierName
                                        )}
                                      />
                                      <div className="form-text">
                                        ( optional )
                                      </div>
                                      <Form.Control.Feedback type="invalid">
                                        Please enter at least 2 characters or leave
                                        it blank.
                                      </Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="currentPolicyExpiration">
                                        Policy Expiration Date (optional)
                                      </Form.Label>
                                      <Form.Control
                                        id="currentPolicyExpiration"
                                        type="date"
                                        value={currentPolicyExpiration}
                                        onChange={(e) =>
                                          setCurrentPolicyExpiration(
                                            e.target.value
                                          )
                                        }
                                        {...vp(
                                          "currentPolicyExpiration",
                                          V.currentPolicyExpiration
                                        )}
                                      />
                                      <div className="form-text">
                                        Approximate is fine if you’re not sure.
                                      </div>
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="notes">
                                        Notes (optional)
                                      </Form.Label>
                                      <Form.Control
                                        id="notes"
                                        as="textarea"
                                        rows={3}
                                        value={notes}
                                        onChange={(e) =>
                                          setNotes(e.target.value)
                                        }
                                        placeholder="Anything else we should know? Certificate requirements, additional insureds, special projects, etc."
                                        {...vp("notes", V.notes)}
                                      />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            )}

                            {/* STEP 4: CONFIRMATION */}
                            {currentStep === 3 && (
                              <Col xs={12}>
                                <ConfirmationBlock
                                  status={serverStatus}
                                  app={lastSubmittedApp}
                                />
                              </Col>
                            )}
                          </Row>

                          {/* Wizard controls (no buttons on final step) */}
                          {currentStep < 3 && (
                            <div className="d-flex justify-content-between mt-3">
                              <Button
                                type="button"
                                variant="outline-secondary"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  goBack();
                                }}
                                disabled={currentStep === 0}
                              >
                                Previous
                              </Button>

                              {currentStep < 2 ? (
                                <Button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isStepValid(currentStep)) goNext();
                                  }}
                                  disabled={!isStepValid(currentStep)}
                                >
                                  Next
                                </Button>
                              ) : (
                                <Button
                                  type="submit"
                                  variant="primary"
                                  size="lg"
                                  disabled={submitting || !valid}
                                >
                                  {submitting ? (
                                    <>
                                      <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                      />{" "}
                                      Submitting...
                                    </>
                                  ) : (
                                    <>Get My Quote</>
                                  )}
                                </Button>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        /* ---------- VERTICAL (all-in-one) ---------- */
                        <>
                          {!submitOk ? (
                            <>
                              <Row className="g-3">
                                {/* Contact & Business */}
                                <Col xs={12}>
                                  <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                    <legend
                                      className="px-2 fs-6 text-muted"
                                      style={{
                                        top: "0.5rem",
                                        background: "var(--bs-secondary-bg)",
                                      }}
                                    >
                                      <strong>Contact &amp; Business</strong>
                                    </legend>

                                    <Row className="g-3 mt-1">
                                      <Col md={12}>
                                        <Form.Label htmlFor="fullNameV">
                                          Your Name
                                        </Form.Label>
                                        <Form.Control
                                          id="fullNameV"
                                          value={fullName}
                                          onChange={(e) =>
                                            setFullName(e.target.value)
                                          }
                                          placeholder="Jane Contractor"
                                          required
                                          {...vp("fullName", V.fullName)}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Please enter your name.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={6}>
                                        <Form.Label htmlFor="emailV">
                                          Email
                                        </Form.Label>
                                        <Form.Control
                                          id="emailV"
                                          type="email"
                                          value={email}
                                          onChange={(e) =>
                                            setEmail(e.target.value)
                                          }
                                          placeholder="you@company.com"
                                          required
                                          {...vp("email", V.email)}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Enter a valid email.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={6}>
                                        <Form.Label htmlFor="phoneV">
                                          Phone
                                        </Form.Label>
                                        <Form.Control
                                          id="phoneV"
                                          inputMode="tel"
                                          value={phone}
                                          onChange={(e) =>
                                            setPhone(e.target.value)
                                          }
                                          placeholder="(555) 123-4567"
                                          required
                                          {...vp("phone", V.phone)}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Enter a 10-digit phone.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={8}>
                                        <Form.Label htmlFor="businessNameV">
                                          Painting Business Name
                                        </Form.Label>
                                        <Form.Control
                                          id="businessNameV"
                                          value={businessName}
                                          onChange={(e) =>
                                            setBusinessName(e.target.value)
                                          }
                                          placeholder="ABC Painting, LLC"
                                          required
                                          {...vp("businessName", V.businessName)}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Please enter your business name.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="websiteV">
                                          Website (optional)
                                        </Form.Label>
                                        <Form.Control
                                          id="websiteV"
                                          value={website}
                                          onChange={(e) =>
                                            setWebsite(e.target.value)
                                          }
                                          placeholder="www.abcpainting.com"
                                          {...vpOpt(
                                            "website",
                                            !!website.trim(),
                                            V.website
                                          )}
                                        />
                                      </Col>

                                      <Col md={8}>
                                        <Form.Label htmlFor="address1V">
                                          Business Street Address
                                        </Form.Label>
                                        <Form.Control
                                          id="address1V"
                                          value={address1}
                                          onChange={(e) =>
                                            setAddress1(e.target.value)
                                          }
                                          placeholder="123 Main St"
                                          required
                                          {...vp("address1", V.address1)}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Enter your street address.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="address2V">
                                          Suite / Unit (optional)
                                        </Form.Label>
                                        <Form.Control
                                          id="address2V"
                                          value={address2}
                                          onChange={(e) =>
                                            setAddress2(e.target.value)
                                          }
                                          placeholder="Suite 200"
                                          {...vpOpt(
                                            "address2",
                                            !!address2.trim()
                                          )}
                                        />
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="cityV">
                                          City
                                        </Form.Label>
                                        <Form.Control
                                          id="cityV"
                                          value={city}
                                          onChange={(e) =>
                                            setCity(e.target.value)
                                          }
                                          placeholder="City"
                                          required
                                          {...vp("city", V.city)}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Enter your city.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="stateV">
                                          State
                                        </Form.Label>
                                        <Form.Select
                                          id="stateV"
                                          value={state}
                                          onChange={(e) =>
                                            setState(e.target.value)
                                          }
                                          required
                                          {...vp("state", V.state)}
                                        >
                                          <option value="">Select</option>
                                          {US_STATES.map((s) => (
                                            <option
                                              key={s.code}
                                              value={s.code}
                                            >
                                              {s.name}
                                            </option>
                                          ))}
                                        </Form.Select>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="zipCodeV">
                                          Zip
                                        </Form.Label>
                                        <Form.Control
                                          id="zipCodeV"
                                          inputMode="numeric"
                                          value={zipCode}
                                          onChange={(e) =>
                                            setZipCode(e.target.value)
                                          }
                                          placeholder="#####"
                                          required
                                          {...vp("zip", V.zip)}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Enter a 5-digit zip code.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="yearsInBusinessV">
                                          Years in Business
                                        </Form.Label>
                                        <Form.Control
                                          id="yearsInBusinessV"
                                          inputMode="numeric"
                                          value={yearsInBusiness}
                                          onChange={(e) =>
                                            setYearsInBusiness(
                                              Number(e.target.value)
                                            )
                                          }
                                          placeholder="0"
                                          required
                                          {...vp(
                                            "yearsInBusiness",
                                            V.yearsInBusiness
                                          )}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          0–100 years allowed.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="employeesCountV">
                                          # of Painters / Employees
                                        </Form.Label>
                                        <Form.Control
                                          id="employeesCountV"
                                          inputMode="numeric"
                                          value={employeesCount}
                                          onChange={(e) =>
                                            setEmployeesCount(
                                              Number(e.target.value)
                                            )
                                          }
                                          placeholder="1"
                                          required
                                          {...vp(
                                            "employeesCount",
                                            V.employeesCount
                                          )}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          0–500 allowed.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="workFocusV">
                                          Main Type of Work
                                        </Form.Label>
                                        <Form.Select
                                          id="workFocusV"
                                          value={workFocus}
                                          onChange={(e) =>
                                            setWorkFocus(
                                              e.target.value as WorkFocus
                                            )
                                          }
                                          required
                                          {...vp("workFocus", V.workFocus)}
                                        >
                                          <option value="">Select</option>
                                          <option value="residential">
                                            Residential
                                          </option>
                                          <option value="commercial">
                                            Commercial
                                          </option>
                                          <option value="both">Both</option>
                                        </Form.Select>
                                      </Col>
                                    </Row>
                                  </fieldset>
                                </Col>

                                {/* Coverage & Exposures */}
                                <Col xs={12}>
                                  <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                    <legend
                                      className="px-2 fs-6 text-muted"
                                      style={{
                                        top: "0.5rem",
                                        background: "var(--bs-secondary-bg)",
                                      }}
                                    >
                                      <strong>Coverage &amp; Exposures</strong>
                                    </legend>

                                    <Row className="g-3 mt-1">
                                      <Col md={4}>
                                        <Form.Label htmlFor="glLimitV">
                                          Desired General Liability Limit
                                        </Form.Label>
                                        <Form.Select
                                          id="glLimitV"
                                          value={generalLiabilityLimit}
                                          onChange={(e) =>
                                            setGeneralLiabilityLimit(
                                              e.target.value as GLimit
                                            )
                                          }
                                          required
                                          {...vp(
                                            "generalLiabilityLimit",
                                            V.generalLiabilityLimit
                                          )}
                                        >
                                          <option value="">Select</option>
                                          <option value="300000">
                                            $300,000
                                          </option>
                                          <option value="500000">
                                            $500,000
                                          </option>
                                          <option value="1000000">
                                            $1,000,000
                                          </option>
                                          <option value="2000000">
                                            $2,000,000
                                          </option>
                                        </Form.Select>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="resPctV">
                                          Residential (% of jobs)
                                        </Form.Label>
                                        <Form.Control
                                          id="resPctV"
                                          inputMode="numeric"
                                          value={residentialPercent}
                                          onChange={(e) =>
                                            setResidentialPercent(
                                              Number(e.target.value)
                                            )
                                          }
                                          placeholder="80"
                                          required
                                          {...vp(
                                            "residentialPercent",
                                            V.residentialPercent
                                          )}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          0–100 allowed.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="comPctV">
                                          Commercial (% of jobs)
                                        </Form.Label>
                                        <Form.Control
                                          id="comPctV"
                                          inputMode="numeric"
                                          value={commercialPercent}
                                          onChange={(e) =>
                                            setCommercialPercent(
                                              Number(e.target.value)
                                            )
                                          }
                                          placeholder="20"
                                          required
                                          {...vp(
                                            "commercialPercent",
                                            V.commercialPercent
                                          )}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          0–100 allowed.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="radiusV">
                                          Typical Work Radius (miles)
                                        </Form.Label>
                                        <Form.Control
                                          id="radiusV"
                                          inputMode="numeric"
                                          value={workRadiusMiles}
                                          onChange={(e) =>
                                            setWorkRadiusMiles(
                                              Number(e.target.value)
                                            )
                                          }
                                          placeholder="50"
                                          required
                                          {...vp(
                                            "workRadiusMiles",
                                            V.workRadiusMiles
                                          )}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          1–250 allowed.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="claimsV">
                                          Prior Claims (last 5 years)
                                        </Form.Label>
                                        <Form.Control
                                          id="claimsV"
                                          inputMode="numeric"
                                          value={priorClaimsCount5y}
                                          onChange={(e) =>
                                            setPriorClaimsCount5y(
                                              Number(e.target.value)
                                            )
                                          }
                                          placeholder="0"
                                          required
                                          {...vp(
                                            "priorClaimsCount5y",
                                            V.priorClaimsCount5y
                                          )}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          0–20 allowed.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label>Coverages Needed</Form.Label>
                                        <div className="d-flex flex-column gap-1 pt-1">
                                          <Form.Check
                                            id="workersCompV"
                                            type="checkbox"
                                            label="Workers Compensation"
                                            checked={wantsWorkersComp}
                                            onChange={(e) =>
                                              setWantsWorkersComp(
                                                e.currentTarget.checked
                                              )
                                            }
                                          />
                                          <Form.Check
                                            id="commAutoV"
                                            type="checkbox"
                                            label="Commercial Auto"
                                            checked={wantsCommercialAuto}
                                            onChange={(e) =>
                                              setWantsCommercialAuto(
                                                e.currentTarget.checked
                                              )
                                            }
                                          />
                                          <Form.Check
                                            id="toolsEquipV"
                                            type="checkbox"
                                            label="Tools &amp; Equipment"
                                            checked={wantsToolsEquipment}
                                            onChange={(e) =>
                                              setWantsToolsEquipment(
                                                e.currentTarget.checked
                                              )
                                            }
                                          />
                                        </div>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label>Where You Work</Form.Label>
                                        <div className="d-flex flex-column gap-1 pt-1">
                                          <Form.Check
                                            id="interiorWorkV"
                                            type="checkbox"
                                            label="Interior Work"
                                            checked={doesInteriorWork}
                                            onChange={(e) =>
                                              setDoesInteriorWork(
                                                e.currentTarget.checked
                                              )
                                            }
                                          />
                                          <Form.Check
                                            id="exteriorWorkV"
                                            type="checkbox"
                                            label="Exterior Work"
                                            checked={doesExteriorWork}
                                            onChange={(e) =>
                                              setDoesExteriorWork(
                                                e.currentTarget.checked
                                              )
                                            }
                                          />
                                          <Form.Check
                                            id="aboveTwoStoriesV"
                                            type="checkbox"
                                            label="Work Above 2 Stories"
                                            checked={worksAboveTwoStories}
                                            onChange={(e) =>
                                              setWorksAboveTwoStories(
                                                e.currentTarget.checked
                                              )
                                            }
                                          />
                                        </div>
                                      </Col>
                                    </Row>
                                  </fieldset>
                                </Col>

                                {/* Details & Notes */}
                                <Col xs={12}>
                                  <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                    <legend
                                      className="px-2 fs-6 text-muted"
                                      style={{
                                        top: "0.5rem",
                                        background: "var(--bs-secondary-bg)",
                                      }}
                                    >
                                      <strong>Details, Carrier &amp; Notes</strong>
                                    </legend>

                                    <Row className="g-3 mt-1">
                                      <Col md={6}>
                                        <Form.Label htmlFor="servicesOfferedV">
                                          Types of Painting You Do (optional)
                                        </Form.Label>
                                        <Form.Control
                                          id="servicesOfferedV"
                                          as="textarea"
                                          rows={3}
                                          value={servicesOffered}
                                          onChange={(e) =>
                                            setServicesOffered(e.target.value)
                                          }
                                          placeholder="e.g., Interior, exterior, cabinets, decks, new construction, repaints..."
                                          {...vp(
                                            "servicesOffered",
                                            V.servicesOffered
                                          )}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Please enter at least 3 characters or
                                          leave it blank.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={6}>
                                        <Form.Label htmlFor="highRiskWorkV">
                                          Any high-risk work? (optional)
                                        </Form.Label>
                                        <Form.Control
                                          id="highRiskWorkV"
                                          as="textarea"
                                          rows={3}
                                          value={highRiskWork}
                                          onChange={(e) =>
                                            setHighRiskWork(e.target.value)
                                          }
                                          placeholder="e.g., High-rise, industrial plants, lead paint removal..."
                                          {...vp(
                                            "highRiskWork",
                                            V.highRiskWork
                                          )}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Please enter at least 3 characters or
                                          leave it blank.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={6}>
                                        <Form.Label htmlFor="currentCarrierNameV">
                                          Current Insurance Company (optional)
                                        </Form.Label>
                                        <Form.Control
                                          id="currentCarrierNameV"
                                          value={currentCarrierName}
                                          onChange={(e) =>
                                            setCurrentCarrierName(
                                              e.target.value
                                            )
                                          }
                                          placeholder="If currently insured"
                                          {...vp(
                                            "currentCarrierName",
                                            V.currentCarrierName
                                          )}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          Please enter at least 2 characters or
                                          leave it blank.
                                        </Form.Control.Feedback>
                                      </Col>

                                      <Col md={6}>
                                        <Form.Label htmlFor="currentPolicyExpirationV">
                                          Policy Expiration Date (optional)
                                        </Form.Label>
                                        <Form.Control
                                          id="currentPolicyExpirationV"
                                          type="date"
                                          value={currentPolicyExpiration}
                                          onChange={(e) =>
                                            setCurrentPolicyExpiration(
                                              e.target.value
                                            )
                                          }
                                          {...vp(
                                            "currentPolicyExpiration",
                                            V.currentPolicyExpiration
                                          )}
                                        />
                                      </Col>

                                      <Col md={12}>
                                        <Form.Label htmlFor="notesV">
                                          Notes (optional)
                                        </Form.Label>
                                        <Form.Control
                                          id="notesV"
                                          as="textarea"
                                          rows={3}
                                          value={notes}
                                          onChange={(e) =>
                                            setNotes(e.target.value)
                                          }
                                          placeholder="Anything else we should know? Certificate requirements, additional insureds, special projects, etc."
                                          {...vp("notes", V.notes)}
                                        />
                                      </Col>
                                    </Row>
                                  </fieldset>
                                </Col>
                              </Row>

                              {/* Submit */}
                              <div className="d-grid mt-3">
                                <Button
                                  type="submit"
                                  variant="primary"
                                  size="lg"
                                  disabled={!valid || submitting}
                                >
                                  {submitting ? (
                                    <>
                                      <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                      />{" "}
                                      Submitting...
                                    </>
                                  ) : (
                                    <>Get My Quote</>
                                  )}
                                </Button>
                                <div
                                  className="text-center mt-2 text-body-secondary"
                                  style={{ fontSize: "0.9rem" }}
                                >
                                  By clicking, you agree we may contact you about
                                  insurance options. No spam. You can opt out
                                  anytime.
                                </div>
                              </div>
                            </>
                          ) : (
                            <ConfirmationBlock
                              status={serverStatus}
                              app={lastSubmittedApp}
                            />
                          )}
                        </>
                      )}
                    </Form>
                    {/* PAINTERS FORM END */}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* How It Works */}
        <section className="py-5 bg-light border-top border-bottom">
          <Container>
            <Row className="mb-4">
              <Col lg={6}>
                <h2 className="fw-bold mb-1">How It Works</h2>
                <p className="text-body-secondary mb-0">
                  Three simple steps to protect your painting business.
                </p>
              </Col>
            </Row>
            <Row className="g-4">
              <Col md={4}>
                <div className="d-flex align-items-start gap-3">
                  <div className="badge rounded-pill text-bg-primary fs-6 mt-1">
                    1
                  </div>
                  <div>
                    <h6 className="mb-1">Tell us about your business</h6>
                    <p className="mb-0 text-body-secondary">
                      Share your business name, location, crew size, and type of
                      work you do.
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="d-flex align-items-start gap-3">
                  <div className="badge rounded-pill text-bg-primary fs-6 mt-1">
                    2
                  </div>
                  <div>
                    <h6 className="mb-1">Pick the coverage you need</h6>
                    <p className="mb-0 text-body-secondary">
                      Choose liability limits and add workers comp, commercial
                      auto, and tools coverage.
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="d-flex align-items-start gap-3">
                  <div className="badge rounded-pill text-bg-primary fs-6 mt-1">
                    3
                  </div>
                  <div>
                    <h6 className="mb-1">We compare carriers</h6>
                    <p className="mb-0 text-body-secondary">
                      We shop top insurance companies and help you bind the
                      right policy and get COIs fast.
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-5 bg-light border-top">
          <Container>
            <Row className="mb-4">
              <Col lg={8}>
                <h2 className="fw-bold mb-1">
                  Frequently Asked Questions About Painters Insurance
                </h2>
                <p className="text-body-secondary mb-0">
                  Quick answers about painter liability, certificates, and
                  coverage.
                </p>
              </Col>
            </Row>

            <FAQ_Component_v02b_Simple faqs={paintersInsuranceFAQs} openFirst />
          </Container>
        </section>

        {/* INSURANCE CARRIERS */}
        <section id="carrierList" className="py-5 bg-light border-top">
          <Container>
            <Row>
              <Col>
                <InsuranceCarriersList_v01 />
              </Col>
            </Row>
          </Container>
        </section>

        {/* Footer */}
        <footer className="py-4">
          <Container>
            <Row className="align-items-center">
              <Col md={6} className="small text-body-secondary">
                © {new Date().getFullYear()} Discount Painters Insurance. All
                rights reserved. {VERSION}
                <div className="mt-2">
                  Insurance services subject to licensing and state
                  availability. This page is for general information and not
                  legal advice.
                </div>
              </Col>
              <Col
                md={6}
                className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2"
              >
                {AGENT_PHONE_BUTTON_OBJ}
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </>
  );
};

/* ------------------------------------------------------------
   Confirmation block (Sectioned, readable, no JSON)
------------------------------------------------------------ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: IPaintersLeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">
            We don’t have your details to display yet.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const exposureList = [
    app.doesInteriorWork ? "Interior Work" : null,
    app.doesExteriorWork ? "Exterior Work" : null,
    app.worksAboveTwoStories ? "Above 2 Stories" : null,
  ].filter(Boolean) as string[];

  const coverageList = [
    "General Liability",
    app.wantsWorkersComp ? "Workers Comp" : null,
    app.wantsCommercialAuto ? "Commercial Auto" : null,
    app.wantsToolsEquipment ? "Tools & Equipment" : null,
  ].filter(Boolean) as string[];

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {/* Status */}
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">
                Thank you! A licensed representative will contact you shortly.
              </div>
              <div className="small">
                Submission status: <strong>{status?.status ?? 200}</strong>
                {status?.id ? (
                  <>
                    {" "}
                    • Reference ID: <strong>{status.id}</strong>
                  </>
                ) : null}
              </div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">
                We couldn’t confirm your submission.
              </div>
              <div className="small">
                {status?.message || "Please try again or give us a call."}
              </div>
            </div>
          </Alert>
        )}

        {/* Sections */}
        <div className="mt-3">
          {/* Contact */}
          <Section title="Contact & Business">
            <KV label="Name" value={dash(app.fullName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={formatPhonePretty(app.phone)} />
            <KV label="Business Name" value={dash(app.businessName)} />
            <KV label="Website" value={dash(app.website)} />
          </Section>

          {/* Location */}
          <Section title="Location">
            <KV label="Street" value={dash(app.address1)} />
            <KV label="Suite / Unit" value={dash(app.address2)} />
            <KV
              label="City / State / Zip"
              value={`${dash(app.city)}, ${dash(app.state)} ${dash(
                app.zipCode
              )}`}
            />
          </Section>

          {/* Business Profile */}
          <Section title="Business Profile">
            <KV
              label="Years in Business"
              value={String(app.yearsInBusiness)}
            />
            <KV
              label="# of Painters / Employees"
              value={String(app.employeesCount)}
            />
            <KV label="Work Focus" value={cap(dash(app.workFocus))} />
            <KV
              label="Residential (% of jobs)"
              value={`${app.residentialPercent ?? 0}%`}
            />
            <KV
              label="Commercial (% of jobs)"
              value={`${app.commercialPercent ?? 0}%`}
            />
            <KV
              label="Typical Work Radius"
              value={
                app.workRadiusMiles
                  ? `${app.workRadiusMiles} miles`
                  : dash(String(app.workRadiusMiles))
              }
            />
          </Section>

          {/* Coverage */}
          <Section title="Coverage Needs">
            <KV
              label="General Liability Limit"
              value={
                app.generalLiabilityLimit
                  ? money(app.generalLiabilityLimit)
                  : "—"
              }
            />
            <KV
              label="Coverages Requested"
              value={coverageList.length ? coverageList.join(", ") : "—"}
            />
            <KV
              label="Current Insurance Company"
              value={dash(app.currentCarrierName)}
            />
            <KV
              label="Policy Expiration"
              value={dash(app.currentPolicyExpiration)}
            />
            <KV
              label="Prior Claims (5y)"
              value={String(app.priorClaimsCount5y)}
            />
          </Section>

          {/* Work & Notes */}
          <Section title="Work & Notes">
            <KV
              label="Where You Work"
              value={exposureList.length ? exposureList.join(", ") : "—"}
            />
            <KV
              label="Types of Painting"
              value={dash(app.servicesOffered)}
            />
            <KV
              label="High-Risk Work"
              value={dash(app.highRiskWork)}
            />
          </Section>

          <Section title="Additional Notes">
            <div className="p-3 bg-body-tertiary border rounded">
              {dash(app.notes)}
            </div>
          </Section>
        </div>

        {/* Actions */}
        <div className="mt-4 d-flex flex-wrap gap-2">
          <div className="d-none d-md-block text-body-secondary me-2">
            Speak with an agent:
          </div>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="NumberOfEmployeesTip">{AGENT_PHONE_HOURS}</Tooltip>}
          >
            <Button variant="outline-primary" href="tel:3033375434">
              {AGENT_PHONE_NUMBER}
            </Button>
          </OverlayTrigger>

          <Button
            variant="outline-secondary"
            onClick={() =>
              window.scrollTo({ top: 0, behavior: "smooth" })
            }
          >
            Back to top
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">
      {children}
    </Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default DISCOUNT_PAINTERS_LandingPage_v01_WIZARD;
